import React, { FC, useState } from 'react';
import { useDataProvider } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import { getDayStartUTC, getDayEndUTC, getDayEnd } from '../../../../../common/utils/time-utils';
import PruTable from '../../../../../common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from '../../../../../common/components/PruTable/PruFilter';
import { PaginateListMore } from 'src/app/common/types/common-types';
import { useLang } from 'src/app/i18n';
import moment from 'moment';
import { CustomerManagementAGViewReportListParams } from '../../../types/customerManagementAGViewReportTypes';
import {
  fetchCustomerManagementAGViewReport,
  fetchCustomerManagementViewAGReportDownload,
} from '../../../network/customerManagementAGViewReportCrud';

type CustomerManagementReportProps = {} & ParamsProps;

const initialState: CustomerManagementAGViewReportListParams = {
  requestedDateFrom: moment(getDayStartUTC(new Date()).setMonth(getDayStartUTC(new Date()).getMonth() - 6)).toDate(),
  requestedDateTo: getDayEnd(new Date()),
  materialName: '',
  agentOfficeCode: '',
  page: 1,
  limit: 5,
  criteria: {},
};

const paramsInitiator = (initialParams?: Record<string, string>): CustomerManagementAGViewReportListParams => {
  return initialParams
    ? {
        requestedDateFrom: initialParams.requestedDateFrom
          ? moment(getDayStartUTC(new Date()).setMonth(getDayStartUTC(new Date()).getMonth() - 6)).toDate()
          : initialState.requestedDateFrom,
        requestedDateTo: initialParams.requestedDateTo ? getDayEndUTC(new Date()) : initialState.requestedDateTo,
        materialName: initialParams.materialName ? initialParams.materialName : '',
        criteria: {},
        agentOfficeCode: initialParams.agentOfficeCode ? initialParams.agentOfficeCode : '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const EventManagementCustomerAGView: FC<CustomerManagementReportProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilterValue = paramsInitiator(initialParams);
  const currentLanguage = useLang();

  const [criterias, setCriterias] = useState([]);

  const handleGetCriteria = (criteriasData: any) => {
    console.log(criteriasData, 'criterias');

    setCriterias(criteriasData);

    setFilterState({
      ...filterState,
      criteria: {
        ...criteriasData,
      },
    });
  };

  const [filterState, setFilterState] = useState<CustomerManagementAGViewReportListParams>(defaultFilterValue);
  const [reportList, setReportList] = useState<PaginateListMore<any>>();
  const { isLoading, refreshData } = useDataProvider<PaginateListMore<any>>(
    async () => {
      try {
        return await fetchCustomerManagementAGViewReport(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvtDownload = () => {
    try {
      fetchCustomerManagementViewAGReportDownload(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('report.customer.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'materialName',
            initialValue: filterState.materialName,
            displayName: Translation('report.customer.filter.event'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentOfficeCode',
            initialValue: filterState.agentOfficeCode,
            displayName: Translation('report.customer.filter.officeCode'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'requestedDateFrom',
            fieldTo: 'requestedDateTo',
            initialDateFrom: filterState.requestedDateFrom,
            initialDateTo: filterState.requestedDateTo,
            defaultDateFrom: defaultFilterValue.requestedDateFrom,
            defaultDateTo: defaultFilterValue.requestedDateTo,
            displayName: Translation('report.customer.filter.eventDate'),
          },
          {
            type: PruFilterItemType.CRITERIA,
            field: 'criteria',
            conditionArr: [
              {
                name: 'Territory',
                propName: 'territory',
                order: 1,
              },
              {
                name: 'BDM',
                propName: 'agentBDMName',
                order: 2,
              },
              {
                name: 'Unit Code',
                propName: 'agentUnitCode',
                order: 3,
              },
              {
                name: 'AG Code',
                propName: 'agentCode',
                order: 4,
              },
              {
                name: 'AG Name',
                propName: 'agentName',
                order: 5,
              },
              {
                name: 'AL Code',
                propName: 'leaderCode',
                order: 6,
              },
              {
                name: 'AL Name',
                propName: 'leaderName',
                order: 7,
              },
              {
                name: 'Campaign Name',
                propName: 'campaignName',
                order: 8,
              },
            ],
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            materialName: data.materialName,
            agentOfficeCode: data.agentOfficeCode,
            requestedDateFrom:
              !data.requestedDateFrom && !data.requestedDateTo
                ? moment(getDayEndUTC(new Date()).setMonth(getDayStartUTC(new Date()).getMonth() - 6)).toDate()
                : !data.requestedDateFrom && data.requestedDateTo
                ? moment(
                    getDayStartUTC(new Date(data.requestedDateTo)).setMonth(
                      getDayStartUTC(new Date(data.requestedDateTo)).getMonth() - 6,
                    ),
                  ).toDate()
                : data.requestedDateFrom,
            requestedDateTo:
              !data.requestedDateFrom && !data.requestedDateTo
                ? getDayEndUTC(new Date())
                : data.requestedDateFrom && !data.requestedDateTo
                ? moment(
                    getDayEndUTC(new Date(data.requestedDateFrom)).setMonth(
                      getDayEndUTC(new Date(data.requestedDateFrom)).getMonth() + 6,
                    ),
                  ).toDate()
                : data.requestedDateTo,
          });
        }}
        customDateRangeMonth={true}
        fetchData={refreshData}
        setCriterias={handleGetCriteria}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.customer.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvtDownload(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            keyIndex: 'territory',
            align: 'center',
            displayName: 'Territory',
            renderData: (row) => (row.territory ? row.territory : '-'),
          },
          {
            keyIndex: 'bdm',
            align: 'center',
            displayName: 'BDM',
            renderData: (row) => (row.agentBDMName ? row.agentBDMName : '-'),
          },
          {
            keyIndex: 'OfficeCode',
            align: 'center',
            displayName: 'Office Code',
            renderData: (row) => (row.agentOfficeCode ? row.agentOfficeCode : '-'),
          },
          {
            keyIndex: 'branchCode',
            align: 'center',
            displayName: 'Branch Code',
            renderData: (row) => (row.agentBranchCode ? row.agentBranchCode : '-'),
          },
          {
            keyIndex: 'UnitCode',
            align: 'center',
            displayName: 'Unit Code',
            renderData: (row) => (row.agentUnitCode ? row.agentUnitCode : '-'),
          },
          {
            keyIndex: 'AgentCode',
            align: 'center',
            displayName: 'Agent Code',
            renderData: (row) => (row.agentCode ? row.agentCode : '-'),
          },
          {
            keyIndex: 'AgentName',
            align: 'center',
            displayName: 'Agent Name',
            renderData: (row) => (row.agentName ? row.agentName : '-'),
          },
          {
            keyIndex: 'AL Code',
            align: 'center',
            displayName: 'AL Code',
            renderData: (row) => (row.leaderCode ? row.leaderCode : '-'),
          },
          {
            keyIndex: 'AL Name',
            align: 'center',
            displayName: 'AL Name',
            renderData: (row) => (row.leaderName ? row.leaderName : '-'),
          },
          {
            keyIndex: 'EventName',
            align: 'center',
            displayName: 'Event Name',
            renderData: (row) => row.materialName,
          },
          {
            keyIndex: 'campaignName',
            align: 'center',
            displayName: 'Campaign Name',
            renderData: (row) => (row.campaignName ? row.campaignName : '-'),
          },
          {
            keyIndex: 'EventDateStart',
            align: 'center',
            displayName: 'Event Date and Time Start',
            renderData: (row) =>
              row.materialEventStartDate ? moment(row.materialEventStartDate).format('DD/MM/YYYY HH:mm:ss') : '-',
          },
          {
            keyIndex: 'EventDateEnd',
            align: 'center',
            displayName: 'Event Date and Time End',
            renderData: (row) =>
              row.materialEventEndDate ? moment(row.materialEventEndDate).format('DD/MM/YYYY HH:mm:ss') : '-',
          },
          {
            keyIndex: 'invitationSent',
            align: 'center',
            displayName: '#Invitation Sent',
            renderData: (row) => (row.totalShare ? row.totalShare : 0),
          },
          {
            keyIndex: 'registered',
            align: 'center',
            displayName: '#Registered',
            renderData: (row) => (row.totalRegister ? row.totalRegister : 0),
          },
          {
            keyIndex: 'checkedIn',
            align: 'center',
            displayName: '#Checked-in',
            renderData: (row) => (row.totalCheckedIn ? row.totalCheckedIn : 0),
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default EventManagementCustomerAGView;
